import {v4 as uuid} from "uuid";
import {LabelNumberTextControl, LabelTextAreaControl} from "../../controls";
import {SequenceComponentHelper} from "../../extensions/SequenceComponentHelper";
import Rete from "rete";
import {tempActionSocket} from "../../sockets";
import {RemoveButtonControl} from "../../controls/RemoveButtonControl";
import {NodeSearchControl} from "../../controls/NodeSearchControl";

export class VoiceAnswer {

    constructor(parentComponent, voiceAnswerAdded, voiceAnswerRemoved) {
        this.parentComponent = parentComponent;
        this.onVoiceAnswerAdded = voiceAnswerAdded;
        this.onVoiceAnswerRemoved = voiceAnswerRemoved;
        this.isAdded = false;
    }

    addVoiceAnswer(node, voiceAnswerUUID = null) {
        let isAnswerNodeLoadedFromJSON = true;

        if (voiceAnswerUUID == null) {
            voiceAnswerUUID = uuid();
            isAnswerNodeLoadedFromJSON = false;
        }

        const keywordsControl = new LabelTextAreaControl(
            this.parentComponent.editor,
            SequenceComponentHelper.getVoiceAnswerKeywordKey(voiceAnswerUUID),
            node,
            false,
            "Keywords"
        );

        const scoreControl = new LabelNumberTextControl(
            this.parentComponent.editor,
            SequenceComponentHelper.getVoiceAnswerScoreKey(voiceAnswerUUID),
            node,
            false,
            "Pts",
            !(node.isCloned || !isAnswerNodeLoadedFromJSON)
        );

        const output = new Rete.Output(
            SequenceComponentHelper.getVoiceAnswerOutputKey(voiceAnswerUUID),
            "Action",
            tempActionSocket,
            false
        );

        const removeVoiceAnswerControl = new RemoveButtonControl(
            this.parentComponent.editor,
            SequenceComponentHelper.getVoiceAnswerRemoveButtonKey(voiceAnswerUUID),
            node,
            false,
            () => this.onRemoveVoiceAnswerClick.bind(this, node, voiceAnswerUUID, removeVoiceAnswerControl, keywordsControl, scoreControl, output)
        )

        node
            .addControl(removeVoiceAnswerControl)
            .addControl(keywordsControl)
            .addControl(scoreControl)
            .addOutput(output);

        this.voiceAnswerAdded(node, voiceAnswerUUID);
        node.update();
    }

    onRemoveVoiceAnswerClick(node, voiceAnswerUUID,  removeVoiceAnswerControl, keywordsControl, scoreControl, outputControl) {
        this.removeVoiceAnswer(node, voiceAnswerUUID, removeVoiceAnswerControl, keywordsControl, scoreControl, outputControl)
            .then(r => {
                this.voiceAnswerRemoved(node,voiceAnswerUUID);
            });
    }

    async removeVoiceAnswer(node, voiceAnswerUUID,  removeVoiceAnswerControl, keywordsControl, scoreControl, outputControl) {
        node.removeControl(removeVoiceAnswerControl);
        node.removeControl(keywordsControl);
        node.removeControl(scoreControl);

        outputControl.connections.map(connection => this.parentComponent.editor.removeConnection(connection));
        node.removeOutput(outputControl);

        node.update();
        this.parentComponent.editor.view.updateConnections({node});

        delete node.data[SequenceComponentHelper.getVoiceAnswerKeywordKey(voiceAnswerUUID)];
        delete node.data[SequenceComponentHelper.getVoiceAnswerScoreKey(voiceAnswerUUID)];
    }

    voiceAnswerAdded(node, addedAnswerUUID) {
        // hide 'add text answer component'
        if(node.voiceAnswersIds === undefined) {
            node.voiceAnswersIds = [];
        }
        node.voiceAnswersIds.push(addedAnswerUUID);

        // add default voice answer if not exist yet
        if (node.defaultVoiceAnswerId === undefined) {
            this.addDefaultVoiceAnswer(node);
        }

        // add time limit of the voice answer if not exist yet
        if (node.voiceAnswerTimeLimitId === undefined) {
            this.addVoiceAnswerTimeLimit(node);
        }

        this.isAdded = true;
        this.onVoiceAnswerAdded();
    }

    async voiceAnswerRemoved(node, removedAnswerUUID) {
        // remove answer uuid from node.voiceAnswersIds
        const indexOfRemovedAnswer = node.voiceAnswersIds.indexOf(removedAnswerUUID);
        if (indexOfRemovedAnswer === -1) {
            console.error(`Unexpected error occurred. There is no voice answer of id '${removedAnswerUUID}' to remove.`)
        }
        node.voiceAnswersIds.splice(indexOfRemovedAnswer, 1);

        // check if text answer could be visible and show it if should be
        // show text answer if there is any voice answer left
        if (node.voiceAnswersIds.length === 0) {
            // remove default voice answer
            if (node.defaultVoiceAnswerId === undefined) {
                throw new Error(`Id of default voice answer is undefined. Should be ${removedAnswerUUID} ?`);
            }
            await this.removeDefaultVoiceAnswer(node, node.defaultVoiceAnswerId);
            node.defaultVoiceAnswerId = undefined;

            // remove voice answer time limit
            if (node.voiceAnswerTimeLimitId === undefined) {
                throw new Error(`Id of time limit voice answer is undefined.`);
            }
            await this.removeVoiceAnswerTimeLimit(node, node.voiceAnswerTimeLimitId);
            node.voiceAnswerTimeLimitId = undefined;

            this.isAdded = false;
            this.onVoiceAnswerRemoved();
        }
    }

    addDefaultVoiceAnswer(node, defaultVoiceAnswerUUID = null) {
        if (defaultVoiceAnswerUUID == null) {
            defaultVoiceAnswerUUID = uuid();
        }

        const nodeSearchControl = new NodeSearchControl(
            this.parentComponent.editor,
            SequenceComponentHelper.getDefaultVoiceAnswerNodeSearchKey(defaultVoiceAnswerUUID),
            node,
            false,
            "node: "
        );

        const scoreControl = new LabelNumberTextControl(
            this.parentComponent.editor,
            SequenceComponentHelper.getDefaultVoiceAnswerScoreKey(defaultVoiceAnswerUUID),
            node,
            false,
            "Pts",
            true
        );

        node.defaultVoiceAnswerId = defaultVoiceAnswerUUID;
        node
            .addControl(nodeSearchControl)
            .addControl(scoreControl);
        node.update();
    }

    async removeDefaultVoiceAnswer(node, defaultVoiceAnswerUUID) {
        // find controls and remove
        const nodeSearchControl = node.controls.get(SequenceComponentHelper.getDefaultVoiceAnswerNodeSearchKey(defaultVoiceAnswerUUID));
        const scoreControl = node.controls.get(SequenceComponentHelper.getDefaultVoiceAnswerScoreKey(defaultVoiceAnswerUUID));
        node.removeControl(nodeSearchControl);
        node.removeControl(scoreControl);

        node.update();
        this.parentComponent.editor.view.updateConnections({node});

        delete node.data[SequenceComponentHelper.getDefaultVoiceAnswerNodeSearchKey(defaultVoiceAnswerUUID)];
        delete node.data[SequenceComponentHelper.getDefaultVoiceAnswerScoreKey(defaultVoiceAnswerUUID)];
    }

    addVoiceAnswerTimeLimit(node, voiceAnswerTimeLimitUUID = null) {
        if (voiceAnswerTimeLimitUUID == null) {
            voiceAnswerTimeLimitUUID = uuid();
        }

        const timeLimitControl = new LabelNumberTextControl(
            this.parentComponent.editor,
            SequenceComponentHelper.getVoiceAnswerTimeLimitKey(voiceAnswerTimeLimitUUID),
            node,
            false,
            "Time limit"
        );

        node.voiceAnswerTimeLimitId = voiceAnswerTimeLimitUUID;
        node.addControl(timeLimitControl);
        node.update();
    }

    async removeVoiceAnswerTimeLimit(node, voiceAnswerTimeLimitUUID) {
        // find controls and remove
        const timeLimitControl = node.controls.get(SequenceComponentHelper.getVoiceAnswerTimeLimitKey(voiceAnswerTimeLimitUUID));
        node.removeControl(timeLimitControl);

        node.update();
        this.parentComponent.editor.view.updateConnections({node});

        delete node.data[SequenceComponentHelper.getVoiceAnswerTimeLimitKey(voiceAnswerTimeLimitUUID)];
    }
}